import type { AxiosInstance } from 'axios';

export type AssessmentOutcome = 'complete' | 'stopped_no_viable_printer';

export interface PostProcessingDto {
  steps: string[];
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN' | null;
  explanation: string | null;
}

export interface PartOverviewAnalysis {
  outcome: AssessmentOutcome;
  printabilityLevel: 1 | 2 | 3 | 4 | 5 | null;
  summary: string | null;
  reasoning: string | null;
  keyConcerns: string[];
  postProcessing: PostProcessingDto | null;
  materialExtraction: Record<string, unknown> | null;
  dimensionsExtraction: Record<string, unknown> | null;
  partFunctionExtraction: Record<string, unknown> | null;
  printerFilter: Record<string, unknown> | null;
}

export interface PartOverviewWorkflow {
  id: string;
  name: string;
  currentStep: string;
  createdAt: string | null;
  lastUpdated: string | null;
  lifecycleStatus: string;
}

export interface PartOverview {
  partId: number;
  partName: string;
  partNumber: string;
  type: string;
  partProcessingStatus: string;
  owner: string;
  category: string;
  priority: string | null;
  naturalLanguageMessage: string;
  hasSpec: boolean;
  hasDrawing2d: boolean;
  hasCad3d: boolean;
  fileUrls?: string[];
  /** Signed thumbnail of the part's 2D drawing; null until the analysis renders one. */
  thumbnailUrl?: string | null;
  targetPrice: number | null;
  surfaceFinish?: string | null;
  tolerances?: string | null;
  estimatedRoi: { rangeFrom: number; rangeTo: number };
  workflows: PartOverviewWorkflow[];
}

export interface AssessmentResultItem {
  id: string;
  assessmentRunId: string;
  partId: string;
  outcome: string;
  failureReason: string | null;
  scorePrintability: number | null;
  scoreMaterialFit: number | null;
  scoreCompatibility: number | null;
  durationMs: number | null;
  summary: string | null;
  gfReasoning: string | null;
  flagResults: Record<string, unknown> | null;
  keyConcerns: unknown[] | null;
  postProcessing: PostProcessingDto | null;
  materialExtraction: Record<string, unknown> | null;
  dimensionsExtraction: Record<string, unknown> | null;
  partFunctionExtraction: Record<string, unknown> | null;
  printerFilter: Record<string, unknown> | null;
  /** Signed thumbnail of the part's 2D drawing; null when none was rendered. */
  thumbnailUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AssessmentStageItem {
  stageNumber: number;
  stageName: string;
  status: string;
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface AssessmentStatus {
  status: 'PENDING' | 'RUNNING' | 'COMPLETE' | 'FAILED';
  assessmentRunId: string | null;
  stageNumber: number | null;
  currentStage: string | null;
  completedAt: string | null;
}

export interface PartListItem {
  id: string;
  partNumber: string;
  name: string;
  lifecycleStatus: string;
  processingStatus: string;
  currentStage: string | null;
  outcome: AssessmentOutcome | null;
  category: string;
  description: string | null;
  workflowStatus: string | null;
  rating: number | null;
  roiBadge: string | null;
  assignee: string | null;
  createdAt: string | null;
  hasSpec: boolean;
  hasDrawing2d: boolean;
  hasCad3d: boolean;
  printabilityScore: number | null;
  roiScore: number | null;
  carbonScore: number | null;
  recommendedTechnology: string | null;
  recommendedMaterial: string | null;
  recommendedEquipmentClass: string | null;
  material: string | null;
  technology: string | null;
  supplierId: number | null;
  supplierName: string | null;
  priority: string | null;
  targetPrice: number | null;
  compatibilityScore: number | null;
  materialFitScore: number | null;
  qualificationResult: string | null;
}

export interface Printability2dService {
  getPartOverview: (partId: string) => Promise<PartOverview>;
  getAssessmentStatus: (partId: string) => Promise<AssessmentStatus>;
  getLatestAssessmentResult: (partId: string) => Promise<AssessmentResultItem | null>;
  getAssessmentResults: (partId: string) => Promise<AssessmentResultItem[]>;
  getAssessmentResultByRunId: (runId: string) => Promise<AssessmentResultItem | null>;
  getAssessmentStages: (runId: string) => Promise<AssessmentStageItem[]>;
  getPartsList: (pagination?: {
    cursor?: string;
    limit?: number;
  }) => Promise<{ data: PartListItem[]; nextCursor?: string | null }>;
  deletePart: (partId: string) => Promise<void>;
}

export function createPrintability2dService(client: AxiosInstance): Printability2dService {
  const buildParams = (pagination?: {
    cursor?: string;
    limit?: number;
  }): Record<string, string> => {
    const params: Record<string, string> = {};
    if (pagination?.cursor) params.cursor = pagination.cursor;
    if (pagination?.limit !== undefined) params.limit = String(pagination.limit);
    return params;
  };

  return {
    getPartOverview: async (partId: string) => {
      const { data } = await client.get<PartOverview>(`/purchasers/parts/${partId}/overview`);
      return data;
    },
    getAssessmentStatus: async (partId: string) => {
      const { data } = await client.get<AssessmentStatus>(
        `/purchasers/parts/${partId}/assessment-status`,
      );
      return data;
    },
    getLatestAssessmentResult: async (partId: string) => {
      const { data } = await client.get<AssessmentResultItem | null>(
        `/purchasers/parts/${partId}/assessment-results/latest`,
      );
      return data;
    },
    getAssessmentResults: async (partId: string) => {
      const { data } = await client.get<AssessmentResultItem[]>(
        `/purchasers/parts/${partId}/assessment-results`,
      );
      return data;
    },
    getAssessmentResultByRunId: async (runId: string) => {
      const { data } = await client.get<AssessmentResultItem | null>(
        `/purchasers/assessment-runs/${runId}`,
      );
      return data;
    },
    getAssessmentStages: async (runId: string) => {
      const { data } = await client.get<AssessmentStageItem[]>(
        `/purchasers/assessment-runs/${runId}/stages`,
      );
      return data;
    },
    getPartsList: async (pagination?: { cursor?: string; limit?: number }) => {
      const { data } = await client.get<{
        data: PartListItem[];
        nextCursor?: string | null;
      }>('/purchasers/parts', { params: buildParams(pagination) });
      return data;
    },
    deletePart: async (partId: string) => {
      await client.delete(`/purchasers/parts/${partId}`);
    },
  };
}
